<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import LaisWhatsappConnectionsAPI from 'dashboard/api/laisWhatsappConnections';
import LaisWhatsappOperationsAPI from 'dashboard/api/laisWhatsappOperations';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');
const connections = ref([]);
const activeAction = ref('');
const isSubmitting = ref(false);
const reaction = ref('👍');
const editedText = ref('');
const pollVoteText = ref('');

const responseData = response =>
  response?.data?.data || response?.data || response;
const whatsmeow = computed(
  () => props.message.content_attributes?.whatsmeow || {}
);
const connection = computed(() =>
  connections.value.find(
    item => Number(item.inbox_id) === Number(props.message.inbox_id)
  )
);
const messageId = computed(
  () =>
    props.message.source_id ||
    whatsmeow.value.message_id ||
    whatsmeow.value.message_ids?.[0] ||
    whatsmeow.value.provider_message_id ||
    ''
);
const targetJid = computed(
  () =>
    whatsmeow.value.chat_jid ||
    whatsmeow.value.remote_jid ||
    currentChat.value?.meta?.sender?.identifier ||
    currentChat.value?.meta?.sender?.phone_number ||
    ''
);
const senderJid = computed(
  () =>
    whatsmeow.value.sender_jid ||
    whatsmeow.value.participant_jid ||
    props.message.sender?.identifier ||
    ''
);
const isReady = computed(() =>
  Boolean(connection.value?.id && messageId.value && targetJid.value)
);
const isPoll = computed(
  () =>
    whatsmeow.value.message_kind === 'poll' ||
    whatsmeow.value.provider_message_type === 'poll'
);
const isOutgoing = computed(() => Number(props.message.message_type) === 1);
const close = () => {
  activeAction.value = '';
};
const isOpen = computed({
  get: () => Boolean(activeAction.value),
  set: visible => {
    if (!visible) close();
  },
});
const pollOptions = computed(() =>
  pollVoteText.value
    .split('\n')
    .map(option => option.trim())
    .filter(Boolean)
);
const commonPayload = computed(() => ({
  number: targetJid.value,
  message_id: messageId.value,
  ...(senderJid.value ? { sender_jid: senderJid.value } : {}),
}));

const loadConnections = async () => {
  if (connections.value.length) return;

  try {
    const response = await LaisWhatsappConnectionsAPI.list();
    connections.value = Array(responseData(response));
  } catch (error) {
    useAlert(t('WHATSAPP_ACTIONS.CONNECTION_FAILED'));
  }
};

const open = async action => {
  await loadConnections();
  if (!isReady.value) {
    useAlert(t('WHATSAPP_ACTIONS.UNAVAILABLE'));
    return;
  }

  editedText.value = props.message.content || '';
  pollVoteText.value = '';
  activeAction.value = action;
};

const execute = async (operation, payload, successMessage) => {
  if (!isReady.value) return;

  isSubmitting.value = true;
  try {
    await LaisWhatsappOperationsAPI.execute(
      connection.value.id,
      operation,
      payload
    );
    useAlert(successMessage);
    close();
  } catch (error) {
    useAlert(t('WHATSAPP_ACTIONS.ACTION_FAILED'));
  } finally {
    isSubmitting.value = false;
  }
};

const sendReaction = () =>
  execute(
    'react_message',
    { ...commonPayload.value, reaction: reaction.value },
    t('WHATSAPP_ACTIONS.REACTION_SENT')
  );

const editMessage = () =>
  execute(
    'edit_message',
    {
      number: targetJid.value,
      message_id: messageId.value,
      text: editedText.value.trim(),
    },
    t('WHATSAPP_ACTIONS.EDITED')
  );

const revokeMessage = () =>
  execute('revoke_message', commonPayload.value, t('WHATSAPP_ACTIONS.REVOKED'));

const votePoll = () =>
  execute(
    'send_poll_vote',
    {
      ...commonPayload.value,
      sender_jid: senderJid.value,
      options: pollOptions.value,
      is_from_me: isOutgoing.value,
    },
    t('WHATSAPP_ACTIONS.POLL_VOTED')
  );

defineExpose({ open, isPoll, isOutgoing });
</script>

<template>
  <woot-modal v-model:show="isOpen" :on-close="close">
    <div v-if="activeAction === 'reaction'" class="space-y-4 p-5">
      <div>
        <p class="text-base font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_ACTIONS.REACT_TITLE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_ACTIONS.REACT_DESCRIPTION') }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <Button
          v-for="emoji in ['👍', '❤️', '😂', '😮', '😢', '🙏']"
          :key="emoji"
          :label="emoji"
          :color="reaction === emoji ? 'brand' : 'slate'"
          :variant="reaction === emoji ? 'solid' : 'faded'"
          @click="reaction = emoji"
        />
      </div>
      <input
        v-model="reaction"
        class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
        :placeholder="$t('WHATSAPP_ACTIONS.REACTION_PLACEHOLDER')"
      />
      <div class="flex justify-end gap-2">
        <Button
          :label="$t('WHATSAPP_ACTIONS.CANCEL')"
          color="slate"
          variant="faded"
          @click="close"
        />
        <Button
          :label="$t('WHATSAPP_ACTIONS.SEND_REACTION')"
          :disabled="!reaction.trim()"
          :loading="isSubmitting"
          @click="sendReaction"
        />
      </div>
    </div>

    <div v-else-if="activeAction === 'edit'" class="space-y-4 p-5">
      <div>
        <p class="text-base font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_ACTIONS.EDIT_TITLE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_ACTIONS.EDIT_NOTICE') }}
        </p>
      </div>
      <textarea
        v-model="editedText"
        class="min-h-28 w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
        :placeholder="$t('WHATSAPP_ACTIONS.EDIT_PLACEHOLDER')"
      />
      <div class="flex justify-end gap-2">
        <Button
          :label="$t('WHATSAPP_ACTIONS.CANCEL')"
          color="slate"
          variant="faded"
          @click="close"
        />
        <Button
          :label="$t('WHATSAPP_ACTIONS.SAVE_EDIT')"
          :disabled="!editedText.trim()"
          :loading="isSubmitting"
          @click="editMessage"
        />
      </div>
    </div>

    <div v-else-if="activeAction === 'revoke'" class="space-y-4 p-5">
      <div>
        <p class="text-base font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_ACTIONS.REVOKE_TITLE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_ACTIONS.REVOKE_DESCRIPTION') }}
        </p>
      </div>
      <div class="flex justify-end gap-2">
        <Button
          :label="$t('WHATSAPP_ACTIONS.CANCEL')"
          color="slate"
          variant="faded"
          @click="close"
        />
        <Button
          :label="$t('WHATSAPP_ACTIONS.REVOKE')"
          color="ruby"
          :loading="isSubmitting"
          @click="revokeMessage"
        />
      </div>
    </div>

    <div v-else-if="activeAction === 'pollVote'" class="space-y-4 p-5">
      <div>
        <p class="text-base font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_ACTIONS.POLL_TITLE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_ACTIONS.POLL_DESCRIPTION') }}
        </p>
      </div>
      <textarea
        v-model="pollVoteText"
        class="min-h-28 w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
        :placeholder="$t('WHATSAPP_ACTIONS.POLL_PLACEHOLDER')"
      />
      <div class="flex justify-end gap-2">
        <Button
          :label="$t('WHATSAPP_ACTIONS.CANCEL')"
          color="slate"
          variant="faded"
          @click="close"
        />
        <Button
          :label="$t('WHATSAPP_ACTIONS.VOTE')"
          :disabled="!pollOptions.length || !senderJid"
          :loading="isSubmitting"
          @click="votePoll"
        />
      </div>
    </div>
  </woot-modal>
</template>
